import { ReadSearchApiArg, SearchQueue } from 'lib-search-types';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import {
  useLazyReadSearchInfiniteQuery,
  UseLazyReadSearchInfiniteQueryReturn,
  UseReadSearchInfiniteQueryResult,
} from 'apis/search';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';
import { useCurrentUser } from 'utils/user';

import { useDashboardLandingPage } from '../../../utils/useDashboardLandingPage';

import { useQueueDashboardParamsToApiArg } from './useQueueDashboardParamsToApiArg';

/**
 * A hook that handles fetching and updating dashboard queues based on URL
 * params and other conditions.
 */
export const useQueueDashboardQueues = (): UseLazyReadSearchInfiniteQueryReturn<
  'queues',
  false
> => {
  const storage = useServerStorage('user');
  const { user } = useCurrentUser();
  const { params, replaceParams } = useAppSearchParams('/dashboard/queues');

  const hasAnalyticsAccess = useHasAccess('analytics');
  const { resetLandingPage } = useDashboardLandingPage();

  const { queueParamsToApiArg, queueSortStringToObject } =
    useQueueDashboardParamsToApiArg();

  const [readSearchQueues, result] = useLazyReadSearchInfiniteQuery<'queues'>({
    infinite: false,
  });

  const [readFavorite, readFavoriteResponse] =
    dashboardApi.useLazyReadDashboardFavoriteQuery({
      selectFromResult: (result) => ({
        ...result,
        // Stabilize the favorite queue IDs so reordering alone doesn't trigger a refetch.
        favoriteQueueIdsKey: result.data?.data.favorite.params.queue_ids
          .toSorted()
          .join(),
      }),
    });

  // Handles triggering a new search request whenever the URL params change.
  useEffect(() => {
    let cancelled = false;
    const baseArg = queueParamsToApiArg();

    // If not viewing a favorite, trigger the new search directly.
    if (params.type !== 'favourite') {
      readSearchQueues(baseArg);
      return;
    }

    // If viewing a specific favorite, we need to fetch that favorite first to get the queue IDs,
    // then fetch the corresponding queues.
    if (params.type === 'favourite' && params.id) {
      readFavorite(
        { id: params.id },
        true, // return cached data if available
      )
        .unwrap()
        .then((response) => {
          if (cancelled) return;

          const { favorite } = response.data;
          const { queue_ids = [], sort_by } = favorite.params;

          if (queue_ids.length) {
            const arg: ReadSearchApiArg<'queues'> = {
              ...baseArg,
              filters: {
                strategy: 'all',
                filters: [
                  {
                    field: 'id',
                    operator: 'is_one',
                    values: queue_ids.map((id) => ({ value: id })),
                  },
                ],
              },
              // Fetch all favorite queues at once so we can reorder client-side.
              pageSize: queue_ids.length,
              page: undefined,
              // If there is a sort_by param in the URL, it takes precedence over anything else.
              ...(!params.sort_by && {
                // If no sort_by in URL, use favorite's sort if any, otherwise undefined, which
                // results in the queues being returned in the order of the queue_ids array.
                sortBy: sort_by ? queueSortStringToObject(sort_by) : undefined,
              }),
            };

            readSearchQueues(arg);
          }

          ampli.dashboardFavoriteViewed({
            shared: favorite.shared,
            isOwner: favorite.user.id === user.id,
          });
        })
        // Failed to fetch the favorite (e.g. it doesn't exist or user doesn't have access).
        .catch(() => {
          if (cancelled) return;

          const { landingPage } = storage.data.dashboard ?? {};

          // If the user's landing page was set to the invalid favorite, reset it to avoid
          // navigating to it again in the future.
          if (landingPage?.url?.path === '/dashboard/queues') {
            const { type, id } = landingPage.url.params.search ?? {};

            if (type === 'favourite' && id === params.id) {
              resetLandingPage();
            }
          }

          // Redirect to the default view.
          replaceParams({ type: hasAnalyticsAccess ? 'all' : 'agent' });
        });
    }

    return () => {
      cancelled = true;
    };
    // We only care when URL params change, or when the favorite queue IDs change (e.g. if the user
    // adds or removes queues from the currently active favorite).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, readFavoriteResponse.favoriteQueueIdsKey]);

  const enhancedResult = useMemo((): UseReadSearchInfiniteQueryResult<
    'queues',
    false
  > => {
    if (params.type === 'favourite') {
      // Show the loading state while we're fetching the selected favorite.
      if (readFavoriteResponse.isFetching) {
        return {
          ...result,
          isFetching: true,
          isLoading: !result.results.length,
        };
      }

      const favoriteParams = readFavoriteResponse.data?.data.favorite.params;

      // Show the empty state immediately if the favorite has no queues.
      // This is necessary because the search endpoint is not called
      // when there are no queue IDs, so we need to override the state here.
      if (!params.id || favoriteParams?.queue_ids?.length === 0) {
        return {
          ...result,
          results: [],
          isUninitialized: false,
          isLoading: false,
          isFetching: false,
          isSuccess: true,
          isError: false,
        };
      }

      // Client-side ordering: the Search API does not guarantee the order of
      // results to match the requested queue IDs, so we reorder them here to
      // preserve the custom ordering defined in the favorite.
      if (
        favoriteParams?.queue_ids?.length &&
        !favoriteParams.sort_by &&
        !params.sort_by
      ) {
        return {
          ...result,
          results: favoriteParams.queue_ids
            .map((id) => result.results.find((q) => q.id === id))
            .filter((queue): queue is SearchQueue => queue !== undefined),
        };
      }

      return result;
    }

    return result;
  }, [params.id, params.sort_by, params.type, readFavoriteResponse, result]);

  return [readSearchQueues, enhancedResult];
};
